/**
 * The plans and what each one unlocks: one table, read by the API's guards
 * and by the dashboard, so a gate and the screen explaining it cannot
 * disagree. Shared with the SPA, so nothing here may import server code.
 *
 * The table follows docs/research/plans-and-regional-pricing.md: Free is the
 * Weekly Sonar and a delayed feed, Alerts sells timing, Pro sells depth.
 */

/** What `subscriber.plan` can hold, lowest first. */
export const PLANS = ['FREE', 'ALERTS', 'PRO'] as const;
export type Plan = (typeof PLANS)[number];

/** A plan somebody pays for. */
export type PaidPlan = Exclude<Plan, 'FREE'>;

export const PLAN_NAMES: Record<Plan, string> = {
  FREE: 'Free',
  ALERTS: 'Alerts',
  PRO: 'Pro',
};

/**
 * A stored or received plan as one of ours. Anything unknown reads as FREE:
 * a value this build has never heard of must not open a paid feature.
 */
export function toPlan(value: unknown): Plan {
  return (PLANS as readonly unknown[]).includes(value)
    ? (value as Plan)
    : 'FREE';
}

/** True when `plan` is `needed` or above it. */
export function planAtLeast(plan: unknown, needed: Plan): boolean {
  return PLANS.indexOf(toPlan(plan)) >= PLANS.indexOf(needed);
}

/**
 * What a plan can gate.
 *
 * - LIVE_FEED: raises the moment a poll sees them; without it the feed holds
 *   each one back two days (freeFeedRelease in src/feed/feed.rules.ts).
 * - ALL_FILTERS: every feed filter; without it, region and status.
 * - ALERTS: alert rules, a raise's alert switches, and the emails they send.
 * - WATCHLIST: watching, the portfolio, and the watched raises' reminders.
 * - CALENDAR: the calendar and its ICS export.
 * - FINANCIALS: the issuer's stated financials on a deal page.
 * - MOMENTUM: raise-progress history and similar raises on a deal page.
 * - EXPORT: the Excel exports, the watchlist's CSV, and the workbooks the
 *   Weekly Sonar and closing-soon emails carry.
 */
export const FEATURES = [
  'LIVE_FEED',
  'ALL_FILTERS',
  'ALERTS',
  'WATCHLIST',
  'CALENDAR',
  'FINANCIALS',
  'MOMENTUM',
  'EXPORT',
] as const;
export type Feature = (typeof FEATURES)[number];

/** The lowest plan that has each feature. */
export const FEATURE_PLAN: Record<Feature, PaidPlan> = {
  LIVE_FEED: 'ALERTS',
  ALL_FILTERS: 'ALERTS',
  ALERTS: 'ALERTS',
  WATCHLIST: 'ALERTS',
  CALENDAR: 'ALERTS',
  FINANCIALS: 'PRO',
  MOMENTUM: 'PRO',
  EXPORT: 'PRO',
};

export function can(plan: unknown, feature: Feature): boolean {
  return planAtLeast(plan, FEATURE_PLAN[feature]);
}

/**
 * Alert rules a subscriber may keep, by plan. Pro's is the cap every account
 * has always had: ample for a person, a stop for a script.
 */
export const RULE_LIMIT: Record<Plan, number> = { FREE: 0, ALERTS: 5, PRO: 20 };

export function ruleLimit(plan: unknown): number {
  return RULE_LIMIT[toPlan(plan)];
}

/**
 * The body of the API's 402: which plan the refused request needs. The
 * dashboard reads `requires` to say what to upgrade to.
 */
export interface PlanRequiredBody {
  statusCode: 402;
  code: 'PLAN_REQUIRED';
  requires: PaidPlan;
  message: string;
}

/** Each feature as the subject of "… part of the Alerts plan.", with its verb. */
const FEATURE_SUBJECT: Record<Feature, string> = {
  LIVE_FEED: 'The live feed is',
  ALL_FILTERS: 'Every feed filter is',
  ALERTS: 'Alert rules and raise alerts are',
  WATCHLIST: 'The watchlist is',
  CALENDAR: 'The calendar is',
  FINANCIALS: 'Stated financials are',
  MOMENTUM: 'Momentum and similar raises are',
  EXPORT: 'Exports are',
};

/** What the gate says when a feature needs a higher plan. */
export function planRequiredMessage(feature: Feature): string {
  return `${FEATURE_SUBJECT[feature]} part of the ${PLAN_NAMES[FEATURE_PLAN[feature]]} plan.`;
}
